/**
 * Google Login Hook
 * Provides Google Sign-In functionality using Google Identity Services
 */

import { useCallback, useEffect, useRef } from 'react';
import { storefrontLayoutLogger } from '@/lib/logger';

interface GoogleLoginOptions {
  onSuccess: (credential: string) => void;
  onError: (error: Error) => void;
}

interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: GoogleCredentialResponse) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              logo_alignment?: 'left' | 'center';
              width?: number;
            }
          ) => void;
          prompt: () => void;
        };
      };
    };
  }
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

export function useGoogleLogin({ onSuccess, onError }: GoogleLoginOptions) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  const handleCredentialResponse = useCallback(
    (response: GoogleCredentialResponse) => {
      try {
        if (response.credential) {
          onSuccess(response.credential);
        } else {
          onError(new Error('No credential received from Google'));
        }
      } catch (error) {
        onError(error instanceof Error ? error : new Error('Unknown error'));
      }
    },
    [onSuccess, onError]
  );

  useEffect(() => {
    // Load Google Identity Services script
    if (!window.google && !initialized.current) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        initializeGoogle();
      };
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    } else if (window.google && !initialized.current) {
      initializeGoogle();
    }
  }, []);

  const initializeGoogle = useCallback(() => {
    if (!window.google || initialized.current) return;

    if (!GOOGLE_CLIENT_ID) {
      storefrontLayoutLogger.warn('Google Client ID not configured');
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      if (buttonRef.current) {
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: buttonRef.current.offsetWidth,
        });
      }

      initialized.current = true;
    } catch (error) {
      storefrontLayoutLogger.error('Failed to initialize Google Sign-In', error instanceof Error ? error : undefined);
      onError(error instanceof Error ? error : new Error('Failed to initialize Google Sign-In'));
    }
  }, [handleCredentialResponse, onError]);

  return { buttonRef };
}
